import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useWeb3Modal } from "@web3modal/wagmi/react";

type Props = {
  onEmailLogin: (email: string, password: string) => Promise<void>;
};

const LoginPage = ({ onEmailLogin }: Props) => {
  const { open } = useWeb3Modal();

  const [isConnecting, setIsConnecting] = useState(false);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    setError(null);
    setIsConnecting(true);
    try {
      await open();
    } catch (connectError) {
      console.error("Failed to open wallet connection modal", connectError);
      setError("Unable to open wallet modal. Please try again.");
    } finally {
      setIsConnecting(false);
    }
  };

  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedEmail = email.trim();
    const normalizedPassword = password.trim();

    if (!normalizedEmail || !normalizedPassword) {
      setError("Enter both email and password.");
      return;
    }

    setError(null);
    setIsSubmittingEmail(true);

    try {
      await onEmailLogin(normalizedEmail, normalizedPassword);
    } catch (loginError) {
      console.error("Failed to log in with email", loginError);
      setError(
        loginError instanceof Error
          ? loginError.message
          : "Invalid email or password.",
      );
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100dvh",
        minHeight: "100dvh",
        maxHeight: "100dvh",
        boxSizing: "border-box",
        px: {
          xs: 1.5,
          sm: 2.5,
        },
        py: {
          xs: 2,
          sm: 3,
        },
        overflow: "hidden",
        backgroundColor: "#060b12",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 520,
          borderRadius: 3,
          border: "1px solid rgba(126, 153, 189, 0.3)",
          backgroundColor: "rgba(10, 18, 28, 0.92)",
          p: {
            xs: 2,
            sm: 3,
          },
          backdropFilter: "blur(8px)",
          boxSizing: "border-box",
        }}
      >
        <Stack spacing={2}>
          <Stack spacing={1.2} alignItems="flex-start">
            <Box
              component="img"
              src="/ninfaLogo.png"
              alt="Ninfa logo"
              sx={{
                width: {
                  xs: 42,
                  sm: 48,
                },
                height: {
                  xs: 42,
                  sm: 48,
                },
                objectFit: "contain",
              }}
            />
            <Typography
              sx={{
                color: "#f5f9ff",
                fontWeight: 800,
                letterSpacing: "0.01em",
                fontSize: {
                  xs: "1.4rem",
                  sm: "1.7rem",
                },
              }}
            >
              Ninfa Admin
            </Typography>
            <Typography sx={{ color: "rgba(214, 232, 255, 0.76)" }}>
              Sign in with email and password or connect your wallet to access the admin dashboard.
            </Typography>
          </Stack>

          {error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box component="form" onSubmit={handleEmailSubmit}>
            <Stack spacing={1.5}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                fullWidth
                disabled={isSubmittingEmail}
                sx={{
                  ".MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "rgba(18, 31, 46, 0.72)",
                  },
                }}
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                fullWidth
                disabled={isSubmittingEmail}
                sx={{
                  ".MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "rgba(18, 31, 46, 0.72)",
                  },
                }}
              />
              <Button
                type="submit"
                disabled={isSubmittingEmail}
                variant="contained"
                size="large"
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  py: 1.1,
                  px: 2,
                  alignSelf: "flex-start",
                  fontWeight: 700,
                  backgroundColor: "#72ddf7",
                  color: "#08212d",
                  "&:hover": {
                    backgroundColor: "#90e8fb",
                  },
                }}
                startIcon={
                  isSubmittingEmail ? (
                    <CircularProgress size={16} sx={{ color: "#08212d" }} />
                  ) : undefined
                }
              >
                {isSubmittingEmail ? "Signing in..." : "Sign In"}
              </Button>
            </Stack>
          </Box>

          <Divider sx={{ borderColor: "rgba(126, 153, 189, 0.22)" }}>or</Divider>

          <Button
            onClick={handleLogin}
            disabled={isConnecting}
            variant="outlined"
            size="large"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              py: 1.1,
              px: 2,
              alignSelf: "flex-start",
              fontWeight: 700,
              borderColor: "rgba(114, 221, 247, 0.4)",
              color: "#f5f9ff",
              "&:hover": {
                borderColor: "#72ddf7",
                backgroundColor: "rgba(114, 221, 247, 0.08)",
              },
            }}
            startIcon={
              isConnecting ? (
                <CircularProgress size={16} sx={{ color: "#f5f9ff" }} />
              ) : undefined
            }
          >
            {isConnecting ? "Connecting..." : "Connect Wallet"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default LoginPage;
